import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Form, FormField, FormTextArea, FormCheckbox } from 'components/form';
import { emailSchema, nameSchema, phoneSchema } from 'utils/validators';

import { sendMessage } from '../../api';
import { formatResponseTime } from '../helpers';
import { GlobalScrollLock } from 'components/GlobalScrollLock';
import * as styled from '../styles/pitch-modal';

const DEFAULT_PITCH_QUESTIONS: Kudable.PitchQuestion[] = [
  {
    id: 'default-budget',
    label: "What's the budget?",
    type: 'number',
    required: true,
    placeholder: 'e.g. 5000',
  },
  {
    id: 'default-promoting',
    label: 'What are you promoting?',
    type: 'short_text',
    required: true,
    placeholder: 'Product / brand / campaign',
  },
  {
    id: 'default-timeline',
    label: 'Timeline',
    type: 'short_text',
    required: false,
    placeholder: 'e.g. 2 weeks starting June 1',
  },
  {
    id: 'default-deliverables',
    label: 'Deliverables expected',
    type: 'long_text',
    required: false,
    placeholder: 'e.g. 2 reels + 3 stories',
  },
];

type Props = {
  token: string;
  providerName: string;
  responseTimeHours: number | null;
  questions: Kudable.PitchQuestion[];
  accentColor: string;
  onClose: () => void;
};

export const PitchModal = ({
  token,
  providerName,
  responseTimeHours,
  questions,
  accentColor,
  onClose,
}: Props) => {
  const effective = questions.length > 0 ? questions : DEFAULT_PITCH_QUESTIONS;

  const dynamicSchema = useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {
      client_name: nameSchema,
      client_phone: phoneSchema,
      client_email: emailSchema,
      phone_is_whatsapp: z.boolean(),
      anything_else: z.string().max(2000, 'Too long'),
    };
    effective.forEach((question) => {
      let questionSchema: z.ZodString | z.ZodOptional<z.ZodString> =
        question.type === 'number'
          ? z.string().regex(/^\d+(\.\d+)?$/, 'Numbers only')
          : question.type === 'url'
            ? z.string().url('Must be a valid URL')
            : z.string();
      questionSchema = questionSchema.max(2000, 'Too long');
      shape[`q_${question.id}`] = question.required
        ? questionSchema.min(1, 'Required')
        : questionSchema.optional().or(z.literal(''));
    });
    return z.object(shape);
  }, [effective]);

  type FormValues = z.infer<typeof dynamicSchema>;

  const defaults: any = {
    client_name: '',
    client_phone: '',
    client_email: '',
    phone_is_whatsapp: false,
    anything_else: '',
  };
  effective.forEach((question) => {
    defaults[`q_${question.id}`] = '';
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(dynamicSchema) as any,
    mode: 'onBlur',
    defaultValues: defaults,
  });

  const [step, setStep] = useState<Profile.MessageStep>('form');
  const [viewToken, setViewToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (values: FormValues) => {
    setError(null);

    const lines: string[] = ['BRAND COLLAB PITCH', ''];
    effective.forEach((question) => {
      const answer = ((values as any)[`q_${question.id}`] ?? '')
        .toString()
        .trim();
      if (answer) lines.push(`${question.label}\n  ${answer}`);
    });
    const anythingElse = ((values as any).anything_else ?? '')
      .toString()
      .trim();
    if (anythingElse) {
      lines.push('');
      lines.push('Anything else:');
      lines.push(anythingElse);
    }

    return sendMessage(token, {
      client_name: (values as any).client_name.trim(),
      client_phone: (values as any).client_phone.trim(),
      client_email: (values as any).client_email.trim(),
      phone_is_whatsapp: (values as any).phone_is_whatsapp,
      initial_message: lines.join('\n'),
    })
      .then((response) => {
        setViewToken(response.data.thread.view_token);
        setStep('done');
      })
      .catch(() => setError('Could not send your pitch. Please try again.'));
  };

  const responseLabel = formatResponseTime(responseTimeHours);

  const threadUrl = viewToken
    ? `${window.location.origin}/messages/thread/${viewToken}`
    : null;

  return (
    <styled.Overlay onClick={onClose}>
      <GlobalScrollLock />
      <styled.SheetOuter onClick={(event) => event.stopPropagation()}>
        <styled.Sheet>
          {step === 'form' && (
            <Form form={form} onSubmit={handleSubmit}>
              <styled.TopRow>
                <styled.Title>Pitch a brief to {providerName}</styled.Title>
                <styled.CloseBtn type="button" onClick={onClose}>
                  ✕
                </styled.CloseBtn>
              </styled.TopRow>
              {responseLabel && (
                <styled.ResponseHint $accent={accentColor}>
                  ⚡ {responseLabel}
                </styled.ResponseHint>
              )}

              <styled.SectionLabel>Your contact</styled.SectionLabel>
              <FormField
                name="client_name"
                label="Your name *"
                placeholder="Your name"
              />
              <FormField
                name="client_phone"
                type="tel"
                label="Phone *"
                placeholder="+1 555 000 0000"
              />
              <FormCheckbox
                name="phone_is_whatsapp"
                label="This number is on WhatsApp"
              />
              <FormField
                name="client_email"
                type="email"
                label="Work email *"
                placeholder="you@brand.com"
              />

              <styled.SectionLabel>Brief</styled.SectionLabel>
              {effective.map((question) => (
                <PitchField key={question.id} question={question} />
              ))}

              <FormTextArea
                name="anything_else"
                label="Anything else"
                placeholder="Other context, links, attachments, references…"
                rows={3}
              />

              {error && <styled.ErrorText>{error}</styled.ErrorText>}

              <styled.PrimaryBtn
                type="submit"
                $accent={accentColor}
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Sending…' : 'Send pitch'}
              </styled.PrimaryBtn>
            </Form>
          )}

          {step === 'done' && (
            <styled.SuccessBox>
              <styled.SuccessIcon $accent={accentColor}>✓</styled.SuccessIcon>
              <styled.SuccessTitle>Pitch sent</styled.SuccessTitle>
              <styled.SuccessText>
                {providerName} will get your brief right away.
                {responseLabel && (
                  <> They typically respond within that time.</>
                )}
              </styled.SuccessText>
              {threadUrl && (
                <styled.LinkBox>
                  <styled.LinkLabel>Track replies here:</styled.LinkLabel>
                  <styled.LinkUrl href={threadUrl} $accent={accentColor}>
                    {threadUrl}
                  </styled.LinkUrl>
                </styled.LinkBox>
              )}
              <styled.PrimaryBtn $accent={accentColor} onClick={onClose}>
                Done
              </styled.PrimaryBtn>
            </styled.SuccessBox>
          )}
        </styled.Sheet>
      </styled.SheetOuter>
    </styled.Overlay>
  );
};

const PitchField = ({ question }: { question: Kudable.PitchQuestion }) => {
  const label = `${question.label}${question.required ? ' *' : ''}`;
  const name = `q_${question.id}`;
  if (question.type === 'long_text') {
    return (
      <FormTextArea
        name={name}
        label={label}
        placeholder={question.placeholder ?? ''}
        rows={3}
      />
    );
  }
  const inputType =
    question.type === 'number'
      ? 'number'
      : question.type === 'url'
        ? 'url'
        : 'text';
  return (
    <FormField
      name={name}
      type={inputType}
      label={label}
      placeholder={question.placeholder ?? ''}
    />
  );
};
